import React from 'react';
import { Shield, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import Layout from '../../layouts/Layout';

const TermsOfService = () => {
    const lastUpdated = "7th July 2025";

    return (
        <Layout
            title="Terms of Service"
            description="KGC Innovations Ltd Terms of Service - Legal terms and conditions for our web development, mobile app, and digital transformation services in the UK."
            keywords="terms of service, KGC Innovations, UK web development, legal terms, service agreement, liability, warranty, payment terms"
            path="/terms-of-service"
        >
            <div className="min-h-screen bg-white">
            {/* Header */}
            <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center opacity-0 animate-[fadeInUp_0.8s_ease-out_0.2s_forwards]">
                        <div className="inline-flex items-center px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-medium mb-8">
                            <Shield className="w-4 h-4 mr-2" />
                            Legal Terms
                        </div>
                        
                        <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                            Terms of Service
                        </h1>
                        
                        <p className="text-xl text-slate-600 mb-4">
                            The terms and conditions governing our services
                        </p>
                        
                        <div className="flex items-center justify-center text-slate-500">
                            <Calendar className="w-4 h-4 mr-2" />
                            Last updated: {lastUpdated}
                        </div>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-16 bg-white">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="prose prose-lg prose-slate max-w-none">
                        
                        <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 mb-12">
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">Introduction</h2>
                            <p className="text-slate-700 leading-relaxed">
                                These Terms of Service ("Terms") govern your use of our website and services provided by KGC Innovations Ltd ("Company," "we," "our," or "us"). By accessing our website at kgcinnovations.com or engaging our services, you agree to be bound by these Terms.
                            </p>
                        </div>

                        <h2 className="text-2xl font-bold text-slate-900 mb-6">1. Description of Services</h2>
                        <p>KGC Innovations Ltd provides:</p>
                        <ul className="list-disc ml-6 mb-8">
                            <li>Web Development (Custom React & Node.js applications)</li>
                            <li>Mobile App Development (React Native & native development)</li>
                            <li>System Architecture (Backend & database solutions)</li>
                            <li>Digital Transformation (Legacy system modernisation)</li>
                            <li>WordPress Development (Custom themes & plugin development)</li>
                            <li>Maintenance & Support (Ongoing support & optimisation)</li>
                        </ul>

                        <h2 className="text-2xl font-bold text-slate-900 mb-6">2. User Obligations</h2>
                        <p>You agree to:</p>
                        <ul className="list-disc ml-6 mb-8">
                            <li>Provide accurate and complete information</li>
                            <li>Use our services only for lawful purposes</li>
                            <li>Not interfere with or disrupt our services</li>
                            <li>Respect intellectual property rights</li>
                            <li>Maintain the confidentiality of any login credentials</li>
                            <li>Promptly notify us of any security breaches</li>
                        </ul>

                        <h2 className="text-2xl font-bold text-slate-900 mb-6">3. Intellectual Property</h2>
                        
                        <h3 className="text-xl font-semibold text-slate-900 mb-4">Our Intellectual Property</h3>
                        <p className="mb-6">All content, features, and functionality of our website and services, including but not limited to text, graphics, logos, and software, are owned by KGC Innovations Ltd and protected by copyright, trademark, and other laws.</p>
                        
                        <h3 className="text-xl font-semibold text-slate-900 mb-4">Client Work Product</h3>
                        <p className="mb-6">Upon full payment, clients receive ownership of custom-developed work products specifically created for them, excluding our proprietary methodologies, frameworks, and pre-existing intellectual property.</p>
                        
                        <h3 className="text-xl font-semibold text-slate-900 mb-4">Third-Party Components</h3>
                        <p className="mb-8">Some deliverables may include third-party libraries, frameworks, or components subject to their own licenses.</p>

                        <h2 className="text-2xl font-bold text-slate-900 mb-6">4. Payment Terms</h2>
                        
                        <h3 className="text-xl font-semibold text-slate-900 mb-4">Payment Methods</h3>
                        <p className="mb-6">We accept payments via Stripe and GoCardless. All prices are in GBP unless otherwise specified.</p>
                        
                        <h3 className="text-xl font-semibold text-slate-900 mb-4">Payment Schedule</h3>
                        <p>Payment terms will be specified in individual service agreements. Generally:</p>
                        <ul className="list-disc ml-6 mb-6">
                            <li>50% deposit required to commence work</li>
                            <li>Remainder due upon project completion</li>
                            <li>Monthly invoicing for ongoing services</li>
                        </ul>
                        
                        <h3 className="text-xl font-semibold text-slate-900 mb-4">Late Payments</h3>
                        <p className="mb-8">Late payments may incur interest charges of 1.5% per month. We reserve the right to suspend services for overdue accounts.</p>

                        <h2 className="text-2xl font-bold text-slate-900 mb-6">5. Warranties and Disclaimers</h2>
                        
                        <h3 className="text-xl font-semibold text-slate-900 mb-4">Limited Warranty</h3>
                        <p className="mb-6">We warrant that our services will be performed in a professional manner consistent with industry standards. We provide a 90-day warranty period for bug fixes on delivered work product.</p>
                        
                        <h3 className="text-xl font-semibold text-slate-900 mb-4">Disclaimer</h3>
                        <p className="mb-8 font-medium">EXCEPT AS EXPRESSLY SET OUT IN THESE TERMS, OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>

                        <h2 className="text-2xl font-bold text-slate-900 mb-6">6. Limitation of Liability</h2>
                        
                        <h3 className="text-xl font-semibold text-slate-900 mb-4">Liability Cap</h3>
                        <p className="mb-6 font-medium">OUR TOTAL LIABILITY FOR ANY CLAIMS UNDER THESE TERMS SHALL NOT EXCEED £25,000 OR THE TOTAL AMOUNT PAID BY YOU FOR THE SPECIFIC SERVICE GIVING RISE TO THE CLAIM, WHICHEVER IS LOWER.</p>
                        
                        <h3 className="text-xl font-semibold text-slate-900 mb-4">Excluded Damages</h3>
                        <p className="mb-6 font-medium">WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.</p>
                        
                        <h3 className="text-xl font-semibold text-slate-900 mb-4">Exceptions</h3>
                        <p className="mb-8">Nothing in these Terms limits liability for death or personal injury caused by negligence, fraud, or other liability that cannot be excluded under applicable law.</p>

                        <h2 className="text-2xl font-bold text-slate-900 mb-6">7. Indemnification</h2>
                        <p>You agree to indemnify and hold harmless KGC Innovations Ltd from any claims, damages, or expenses arising from:</p>
                        <ul className="list-disc ml-6 mb-8">
                            <li>Your breach of these Terms</li>
                            <li>Your use of our services</li>
                            <li>Content you provide to us</li>
                            <li>Your violation of any third-party rights</li>
                        </ul>

                        <h2 className="text-2xl font-bold text-slate-900 mb-6">8. Termination</h2>
                        <p>Either party may terminate ongoing services with 30 days' written notice. We may terminate immediately if you:</p>
                        <ul className="list-disc ml-6 mb-6">
                            <li>Breach these Terms</li>
                            <li>Fail to pay outstanding invoices</li>
                            <li>Engage in illegal or harmful activities</li>
                        </ul>
                        <p className="mb-8">Upon termination, you remain liable for all outstanding payments, and we will provide reasonable assistance in transitioning services.</p>

                        <h2 className="text-2xl font-bold text-slate-900 mb-6">9. Governing Law and Disputes</h2>
                        <p className="mb-6">These Terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the English courts.</p>
                        <p className="mb-8">We encourage resolution of disputes through good faith negotiation before pursuing formal legal action.</p>

                        <h2 className="text-2xl font-bold text-slate-900 mb-6">10. Changes to Terms</h2>
                        <p className="mb-8">We may update these Terms from time to time. We will notify you of material changes via email or website notice. Your continued use of our services after such changes constitutes acceptance of the new Terms.</p>

                        <h2 className="text-2xl font-bold text-slate-900 mb-6">11. Contact Information</h2>
                        <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
                            <p className="mb-4">If you have questions about these Terms, please contact us:</p>
                            <p>
                                KGC Innovations Ltd<br />
                                Company Registration: 16570829<br />
                                Registered Office: Unit A, 82 James Carter Road, Mildenhall, IP28 7DE, England<br />
                                Email: legal@kgcinnovations.com<br />
                                Phone: +44 (0) 20 7046 0289
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="py-16 bg-slate-50">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-3xl font-bold text-slate-900 mb-6">Questions About Our Terms?</h2>
                    <p className="text-lg text-slate-600 mb-8">
                        Need clarification on our terms of service? We're here to help.
                    </p>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-white rounded-xl p-6 border border-slate-200">
                            <Mail className="w-8 h-8 text-slate-900 mx-auto mb-3" />
                            <div className="font-semibold text-slate-900 mb-1">Compliance</div>
                            <div className="text-slate-600">legal@kgcinnovations.com</div>
                        </div>
                        <div className="bg-white rounded-xl p-6 border border-slate-200">
                            <Phone className="w-8 h-8 text-slate-900 mx-auto mb-3" />
                            <div className="font-semibold text-slate-900 mb-1">Phone</div>
                            <div className="text-slate-600">+44 (0) 20 7046 0289</div>
                        </div>
                        <div className="bg-white rounded-xl p-6 border border-slate-200">
                            <MapPin className="w-8 h-8 text-slate-900 mx-auto mb-3" />
                            <div className="font-semibold text-slate-900 mb-1">Registered Office</div>
                            <div className="text-slate-600 text-sm">England & Wales</div>
                        </div>
                    </div>
                </div>
            </section>

            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .animate-[fadeInUp_0.8s_ease-out_0.2s_forwards] {
                    animation: fadeInUp 0.8s ease-out 0.2s forwards;
                }
            `}</style>
        </div>
        </Layout>
    );
};

export default TermsOfService;